import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { MePayslipResponse } from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import { downloadMyPayslipPdf, listMyPayslips } from '../api/me.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

/**
 * /me/payslips — employee's own payslip list with per-row PDF
 * download. Only finalised runs (approved/paid/voided) are surfaced
 * by the API; drafts are hidden until HR finalises them.
 *
 * `voided` rows are shown with a clear pill so the employee
 * understands when an old payslip was superseded by a corrected v2.
 */
export const myPayslipsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/payslips',
  component: MyPayslipsPage,
});

function MyPayslipsPage(): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['me', 'payslips'],
    queryFn: listMyPayslips,
  });

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <nav className="text-xs text-gray-500">
        <Link to="/me" className="hover:underline">
          My profile
        </Link>{' '}
        / Payslips
      </nav>

      <header className="space-y-1">
        <h1 className="font-serif text-3xl">My payslips</h1>
        <p className="text-sm text-gray-400">
          Finalised payslips only. Drafts in progress are hidden until
          HR approves them.
        </p>
      </header>

      {isLoading ? <Loading /> : null}
      {isError ? <ErrorBanner /> : null}

      {data && data.payslips.length === 0 ? (
        <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
          No payslips yet.
        </div>
      ) : null}

      {data && data.payslips.length > 0 ? (
        <PayslipsTable payslips={data.payslips} />
      ) : null}
    </section>
  );
}

function PayslipsTable({
  payslips,
}: {
  payslips: MePayslipResponse[];
}): JSX.Element {
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-left text-gray-400 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 font-medium">Period</th>
            <th className="px-4 py-3 font-medium">Pay date</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium text-right">Gross</th>
            <th className="px-4 py-3 font-medium text-right">Net</th>
            <th className="px-4 py-3 font-medium w-24"></th>
          </tr>
        </thead>
        <tbody>
          {payslips.map((p) => (
            <tr key={p.id} className="border-t border-gray-900 hover:bg-gray-900/40">
              <td className="px-4 py-3">
                <div className="text-white">
                  {formatDate(p.periodStart)} → {formatDate(p.periodEnd)}
                </div>
                <div className="text-xs text-gray-500">v{p.version}</div>
              </td>
              <td className="px-4 py-3 text-gray-400">{formatDate(p.payDate)}</td>
              <td className="px-4 py-3">
                <RunStatusPill status={p.runStatus} />
              </td>
              <td className="px-4 py-3 text-xs text-right">
                {p.payCurrency
                  ? formatMoney(p.payCurrency.gross, p.payCurrency.currency)
                  : formatMoney(p.grossSalary, p.currency)}
              </td>
              <td className="px-4 py-3 text-xs text-white text-right font-medium">
                {p.payCurrency ? (
                  <>
                    {formatMoney(p.payCurrency.takeHome, p.payCurrency.currency)}
                    {/* The statutory figure is what the regulator filing uses. */}
                    <div className="text-[0.65rem] text-gray-500 font-normal">
                      statutory {formatMoney(p.netSalary, p.currency)}
                    </div>
                  </>
                ) : (
                  formatMoney(p.netSalary, p.currency)
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <PdfButton payslipId={p.id} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PdfButton({ payslipId }: { payslipId: string }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="flex items-center justify-end gap-2">
      {err ? (
        <span className="text-xs text-red-400" role="alert" title={err}>
          Failed
        </span>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setErr(null);
          setBusy(true);
          try {
            await downloadMyPayslipPdf(payslipId);
          } catch (caught) {
            setErr(
              caught instanceof ApiError
                ? caught.message
                : 'Download failed. Try again.',
            );
          } finally {
            setBusy(false);
          }
        }}
        className="text-xs border border-gray-700 rounded px-2 py-1 text-gray-300 hover:bg-gray-900 disabled:opacity-50"
        aria-label={`Download PDF for payslip ${payslipId}`}
      >
        {busy ? '…' : 'PDF'}
      </button>
    </div>
  );
}

function RunStatusPill({
  status,
}: {
  status: 'approved' | 'paid' | 'voided' | string;
}): JSX.Element {
  const styles: Record<string, string> = {
    approved: 'bg-green-950 text-green-400 border-green-900',
    paid: 'bg-blue-950 text-blue-400 border-blue-900',
    voided: 'bg-gray-900 text-gray-500 border-gray-800',
  };
  const cls = styles[status] ?? 'bg-gray-900 text-gray-400 border-gray-800';
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${cls}`}>
      {status}
    </span>
  );
}

function Loading(): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
      Loading payslips...
    </div>
  );
}

function ErrorBanner(): JSX.Element {
  return (
    <Alert variant="error">Could not load your payslips. Try refreshing.</Alert>
  );
}
